import type { SettingsView } from './Settings.types';
import type { ReactElement } from 'react';

import { Box, IconButton, Popover } from '@rango-dev/whale';
import { Settings2 } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getContainer } from '../../utils/generalFunctions/common';

import { SettingsExperimentalFeatures } from './Settings.ExperimentalFeatures';
import { SettingsLanguages } from './Settings.Languages';
import styles from './Settings.module.css';
import { SettingsPreferences } from './Settings.Preferences';
import { SettingsTheme } from './Settings.Theme';

const ICON_SIZE = 18;
const POPOVER_SIDE_OFFSET = 8;
const POPOVER_COLLISION_PADDING = 8;
const PANEL_ACCENT_COLOR = 'blue';

export function Settings(): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<SettingsView>('preferences');

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    // Always reopen on the root view rather than wherever the user left off.
    if (!nextOpen) {
      setView('preferences');
    }
  };

  const backToPreferences = () => setView('preferences');

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger>
        <IconButton
          id="header-settings-icon-btn"
          aria-label={t('Settings')}
          variant="ghost"
          color="cyan"
          radius="full"
          size="3">
          <Settings2 size={ICON_SIZE} />
        </IconButton>
      </Popover.Trigger>
      <Popover.Content
        align="end"
        sideOffset={POPOVER_SIDE_OFFSET}
        collisionPadding={POPOVER_COLLISION_PADDING}
        container={getContainer()}
        width={{ initial: '312px', md: '400px' }}
        maxWidth="calc(100vw - var(--space-6))"
        className={styles.popoverContent}>
        <Box data-accent-color={PANEL_ACCENT_COLOR}>
          {view === 'preferences' && <SettingsPreferences onSelect={setView} />}
          {view === 'theme' && <SettingsTheme onBack={backToPreferences} />}
          {view === 'language' && <SettingsLanguages onBack={backToPreferences} />}
          {view === 'experimental' && <SettingsExperimentalFeatures onBack={backToPreferences} />}
        </Box>
      </Popover.Content>
    </Popover.Root>
  );
}
